import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { Fragment, type JSX } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { TextFormField } from '~components/FormFields/TextFormField';
import ProjectMaterialProgress from '~components/Project/ProjectMaterialProgress/ProjectMaterialProgress';
import ProjectDetailsV2 from '~pages/Sales/Orders/NewOrderForm/ProjectDetailsV2';

import {
  type OrderFormV3FieldValues,
  useOrderFormV3Context,
} from '../OrderFormV3Context';

/**
 * Renders `order.form.project_details`. Render-only: the embedded
 * ProjectDetailsV2 exactly as legacy mounts it (no fork; its effects ship
 * unchanged) plus the read-only project material-totals rows. The rows render from the `projectMaterialTotals` form field the
 * project cascade publishes (now effects/useProjectCascade); useFieldArray
 * is a render subscription, not an effect. Each row's anchor carries the
 * orderFieldMetadata test-id suffixed with its index (the base id alone
 * cannot stay DOM-unique across rows).
 */
export function ProjectDetailsPanel(): JSX.Element {
  const { control, formState } = useFormContext<OrderFormV3FieldValues>();
  const { fields: projectMaterialTotalsFields } = useFieldArray({
    control,
    name: 'projectMaterialTotals',
  });
  const {
    isDepartmentRequired,
    isRateByDepartmentNameRuleOn,
    projectData,
    watchedCompany,
  } = useOrderFormV3Context();

  return (
    <Card sx={{ p: 2, mb: 2 }}>
      <Typography variant="form-section-title" sx={{ mb: 1 }}>
        {t('order.form.project_details')}
      </Typography>

      <Grid container spacing={2}>
        <ProjectDetailsV2
          cascadeEnabled
          companyId={watchedCompany?.id}
          isDepartmentRequired={isDepartmentRequired}
          isRateByDepartmentNameRuleOn={isRateByDepartmentNameRuleOn}
          project={projectData ?? undefined}
          source="order-form"
        />

        {projectMaterialTotalsFields.map((materialTotal, index) => {
          return (
            <Fragment key={`${index}--${materialTotal.id}`}>
              <Grid
                size={{ xs: 6 }}
                data-test-id={`order-form-v3-project-material-totals-${index}`}
              >
                <TextFormField
                  control={control}
                  errors={formState.errors}
                  name={`projectMaterialTotals[${index}].material.name`}
                  label={`${t('form_fields.project_materials')}`}
                  isRequired={false}
                  disabled
                />
              </Grid>

              <Grid size={{ xs: 3 }}>
                <TextFormField
                  control={control}
                  errors={formState.errors}
                  name={`projectMaterialTotals[${index}].totalQuantity`}
                  label={`${t('form_fields.project_quantity')}`}
                  isRequired={false}
                  disabled
                />
              </Grid>

              <Grid size={{ xs: 3 }}>
                <TextFormField
                  control={control}
                  errors={formState.errors}
                  name={`projectMaterialTotals[${index}].unitOfMeasure.name`}
                  label={`${t('form_fields.project_units')}`}
                  isRequired={false}
                  disabled
                />
              </Grid>
            </Fragment>
          );
        })}
      </Grid>

      <ProjectMaterialProgress
        materialTotals={projectData?.projectMaterialTotals ?? []}
        projectSummary={projectData?.projectSummary ?? null}
      />
    </Card>
  );
}
